<template>
  <SoDialog
    v-model="value"
    card-title="Under Review Period"
    data-testid="under-review-period-modal"
  >
    <SoErrorBoundary>
      <template v-if="reviewPeriod">
        <div class="mb-8">
          <SoStatus
            v-if="reviewPeriod.isCancelled"
            :status="new Status('critical', 'Cancelled')"
            data-testid="under-review-period-status-cancelled"
          />

          <SoStatus
            v-else-if="reviewPeriod.isExpired"
            :status="new Status('neutral', 'Expired')"
            data-testid="under-review-period-status-expired"
          />

          <SoStatus
            v-else
            :status="new Status('normal', 'Active')"
            data-testid="under-review-period-status-active"
          />
        </div>
        <SoTextField
          :model-value="reviewPeriod.reason"
          label="Reason"
          readonly
        />

        <div class="d-flex gap-1 my-8">
          <SoTextField
            :model-value="formatDate(reviewPeriod.fromDttm)"
            label="From"
            class="date-picker w-100"
            readonly
          />

          <SoTextField
            :model-value="
              reviewPeriod.toDttm ? formatDate(reviewPeriod.toDttm) : '-'
            "
            label="To"
            class="date-picker w-100"
            readonly
          />

          <SoTextField
            v-if="reviewPeriod.isCancelled && reviewPeriod.cancelledDttm"
            :model-value="formatDate(reviewPeriod.cancelledDttm)"
            label="Cancelled on"
            class="w-100"
            readonly
          />
        </div>
        <h6>Suppressed</h6>
        <div class="d-flex">
          <SoCheckbox
            v-if="reviewPeriod.isDunningSuppressed"
            :model-value="true"
            label="Dunning"
            class="mr-8"
            readonly
          />
          <SoCheckbox
            v-if="reviewPeriod.isBillingSuppressed"
            :model-value="true"
            label="Billing"
            class="mr-8"
            readonly
          />
          <SoCheckbox
            v-if="reviewPeriod.isPaymentCollectionSuppressed"
            :model-value="true"
            label="Payment collection"
            class="mr-8"
            readonly
          />

          <SoCheckbox
            v-if="reviewPeriod.isPropertySuppressed"
            :model-value="true"
            label="Property"
            class="mr-8"
            readonly
          />

          <SoCheckbox
            v-if="reviewPeriod.isPaymentReviewSuppressed"
            :model-value="true"
            label="Payment review"
            readonly
          />
        </div>
        <SoTextarea
          :model-value="reviewPeriod.description"
          label="Description"
          class="mt-8"
          readonly
        />
        <div v-if="reviewPeriod.isActive" class="d-flex mt-8">
          <SoButton
            :loading="removeReviewPeriodMutator.isPending"
            title="Stop review period"
            prepend-icon="delete"
            class="ml-auto"
            data-testid="under-review-period-remove-btn"
            large
            @click="removeReviewPeriod()"
          />
        </div>
      </template>
      <SoAlert
        v-if="submitError"
        type="error"
        title="Failed to cancel review period"
        class="mt-4"
        data-testid="under-review-period-remove-alert"
      >
        {{ submitError }}
      </SoAlert>
    </SoErrorBoundary>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { formatDate } from "@/helpers/dateHelper"
  import SoDialog from "@/components/SoDialog.vue"
  import SoErrorBoundary from "@/components/SoErrorBoundary.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoTextarea from "@/components/formControls/SoTextarea.vue"
  import { IReviewPeriod } from "@/models/reviewPeriod/reviewPeriod.types"
  import { Status } from "@/types/Status"
  import { useSnackbarStore } from "@/stores/snackbar"

  export default defineComponent({
    components: {
      SoDialog,
      SoErrorBoundary,
      SoButton,
      SoTextField,
      SoCheckbox,
      SoAlert,
      SoStatus,
      SoTextarea,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
      reviewPeriodId: {
        type: Number,
        default: undefined,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        snackbar: useSnackbarStore(),
        removeReviewPeriodMutator:
          useCurrentAccountStore().removeReviewPeriod(),
        submitError: null as null | string,
        formatDate,
        Status,
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      reviewPeriod(): IReviewPeriod | undefined {
        return this.current.underReviewPeriods.data.find(
          (rp: IReviewPeriod) => rp.id === this.reviewPeriodId,
        )
      },
    },
    methods: {
      removeReviewPeriod() {
        if (!this.reviewPeriodId) return
        this.submitError = null
        this.removeReviewPeriodMutator
          .mutateAsync({ id: this.reviewPeriodId })
          .then(() => {
            this.snackbar.addSuccess(
              "Successfully cancelled Review Period. It may take some time for the changes to take effect",
            )
            this.value = false // close modal
          })
          .catch((error: unknown) => {
            // The mutator throws on an upstream Error union member. Catch it
            // here so the rejected promise doesn't escape into SoErrorBoundary
            // (which would show a generic "Failed to render content"); surface
            // the real gateway message in the in-modal error alert instead.
            this.submitError =
              error instanceof Error
                ? error.message
                : "Failed to cancel review period"
          })
      },
    },
  })
</script>

<style scoped>
  .date-picker {
    max-width: 150px;
  }
</style>
